import { createSupabaseAdminClient } from "../../supabase/supabase";

/**
 * The slice of a venue email job that its public featured-shows landing page
 * (`/shows/featured/[jobId]`) renders from. The page reads live data — no
 * snapshot — so pre-send edits to the featured set are reflected.
 */
export interface PublicEmailFeaturedJob {
  jobId: string;
  venuePageId: string;
  emailSubject: string;
  focusShowIds: string[];
}

/** The composer caps the featured-events picker at this many shows. */
const MAX_FOCUS_SHOW_IDS = 30;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Deliberately lenient extraction of `focusShowIds` from the customization
 * jsonb. The shape authority (`venueEmailCustomizationSchema`) lives in
 * apps/web's southpaw data layer, which this package cannot import — so the
 * public page validates only the one field it renders from: an array of uuid
 * strings, capped at the composer's picker limit. Anything malformed reads as
 * "no featured shows" rather than an error, mirroring how the jobs list
 * parses customizations leniently on the client.
 */
function extractFocusShowIds(customization: unknown): string[] {
  if (typeof customization !== "object" || customization === null) return [];
  const raw = (customization as Record<string, unknown>).focusShowIds;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (id): id is string => typeof id === "string" && UUID_PATTERN.test(id)
    )
    .slice(0, MAX_FOCUS_SHOW_IDS);
}

/**
 * Fetch the featured-shows slice of a venue email job for its public landing
 * page. Returns null when the job doesn't exist, has no customization row
 * (deleting a job cascades its customization away), or isn't a venue job — and
 * without a query when `jobId` isn't a uuid at all: the id comes straight from
 * a public URL, and Postgres rejects a malformed uuid with an error rather than
 * an empty result.
 *
 * Uses the service-role client — `southpaw_jobs` and
 * `southpaw_job_customizations` are deliberately deny-all under RLS, and the
 * page must resolve for anonymous email recipients. Venue binding (does this
 * job belong to the venue whose host the page is served on?) is the caller's
 * job: it compares the returned venuePageId against the resolved venue.
 */
export async function getEmailFeaturedJobPublic(params: {
  jobId: string;
}): Promise<PublicEmailFeaturedJob | null> {
  if (!UUID_PATTERN.test(params.jobId)) return null;

  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from("southpaw_jobs")
    .select(
      "id, email_subject, venue_page_id, southpaw_job_customizations(customization)"
    )
    .eq("id", params.jobId)
    .maybeSingle();

  if (error) throw error;
  if (!data || !data.venue_page_id) return null;

  // The customization relation is 1:1, but tolerate the array shape some
  // client versions return for embedded relations.
  const customizationRow = Array.isArray(data.southpaw_job_customizations)
    ? data.southpaw_job_customizations[0]
    : data.southpaw_job_customizations;
  if (!customizationRow) return null;

  return {
    jobId: data.id,
    venuePageId: data.venue_page_id,
    emailSubject: data.email_subject ?? "",
    focusShowIds: extractFocusShowIds(customizationRow.customization),
  };
}
